import './domainTestSetup.js';

import React, {createContext, Suspense, useEffect, useState} from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import './index.css';
import { setupDomainTesting } from './utils/domainTestHelper'; // 导入域名测试工具
import './styles/domain-styles.css';  // 使用普通CSS实现域名样式
import './utils/domainInit';  // 导入域名初始化脚本
import reportWebVitals from './reportWebVitals';
import { isMobile } from './utils/isMobile';
import { setupChunkLoadRecovery } from './utils/chunkLoadRecovery';
import ShareRecorded from './pages/share_recorded';
import AboutApp from './pages/about_app';
import PriceUp from './pages/price_up';
import SpaceUpgrade from './pages/space_upgrade';
import ShareWord from './pages/share_word';
import ExpandSpace from './pages/expand_space';
import {JsBridge} from "./utils/JsBridge";
import {isApp} from "./utils/EnvUtils";
import {DeerUser} from "./typings/DeerUser";
import DomainDebugger from './components/DomainDebugger';
import { IS_HUIYA } from './config/domain';
import ErrorBoundary from './components/ErrorBoundary';

const H5Website = React.lazy(() => import('./pages/h5_website'));
const Website = React.lazy(() => import('./pages/website'));
const H5SelfService = React.lazy(() => import('./pages/h5_self_service'));
const H5SelfServiceCreate = React.lazy(() => import('./pages/h5_self_service_create'));
const VipSale = React.lazy(() => import('./pages/h5_vip_sale'));
const BaiduPan = React.lazy(() => import('./pages/h5_self_service_baidu'));
const ShareCourse = React.lazy(() => import('./pages/share_course'));
const ShareCourseOutside = React.lazy(
  () => import('./pages/share_course_outside')
);
const VipShare = React.lazy(() => import('./pages/vip_share'));
const CumulativeInvitation = React.lazy(
  () => import('./pages/cumulative_invitation')
);
const ReceiveReward = React.lazy(() => import('./pages/receive_reward'));
const AccountDetails = React.lazy(() => import('./pages/account_details'));
const ActivityDescription = React.lazy(
  () => import('./pages/activity_description')
);
const DailyPaper = React.lazy(() => import('./pages/daily_paper'));
const LearningReportDetail = React.lazy(
  () => import('./pages/learning_report_detail')
);
const VipShareActivity = React.lazy(() => import('./pages/vip_share_activity'));
const GreatTracher = React.lazy(() => import('./pages/great_teacher'));
const FormTeacher = React.lazy(() => import('./pages/form_teacher'));
const NYSpecial = React.lazy(() => import('./pages/ny_special'));
const RegularActivity = React.lazy(() => import('./pages/regular_activity'));
const PriceDiffPay = React.lazy(() => import('./pages/price_diffPay'));
const PriceDiffPayHy = React.lazy(() => import('./pages/price_diffPay_hy'));
const LearningReport = React.lazy(() => import('./pages/learning_report'));
const BookingPlatform = React.lazy(() => import('./pages/booking-platform'));
const BookingCategory = React.lazy(() => import('./pages/booking-platform/booking-category'));
const TeacherDetail = React.lazy(() => import('./pages/booking-platform/teacher-detail'));
const TeacherEdit = React.lazy(() => import('./pages/booking-platform/teacher-edit'));
const TeacherAdd = React.lazy(() => import('./pages/booking-platform/teacher-add'));
const StudentEdit = React.lazy(() => import('./pages/booking-platform/student-edit'));
const CourseDetail = React.lazy(() => import('./pages/booking-platform/course-detail'));
const TextbookSelect = React.lazy(() => import('./pages/booking-platform/textbook-select'));
const BookDetail = React.lazy(() => import('./pages/booking-platform/book-detail'));
const BookingConfirm = React.lazy(() => import('./pages/booking-platform/booking-confirm'));
const BookingLogin = React.lazy(() => import('./pages/booking-platform/login'));
const FavoritesPage = React.lazy(() => import('./pages/booking-platform/favorites'));
const FeedbackPage = React.lazy(() => import('./pages/booking-platform/feedback'));
const HelpCenterPage = React.lazy(() => import('./pages/booking-platform/help-center'));
const ManagedTeachersPage = React.lazy(() => import('./pages/booking-platform/managed-teachers'));
const TeacherSchedulePage = React.lazy(() => import('./pages/booking-platform/teacher-schedule'));
const PointsRecordPage = React.lazy(() => import('./pages/booking-platform/points-record'));
const AdminCoursesPage = React.lazy(() => import('./pages/booking-platform/admin-courses'));
const RechargePage = React.lazy(() => import('./pages/booking-platform/recharge'));
const InteractiveWordCard = React.lazy(() => import('./pages/interactive_word_card'));
const RazInteractive = React.lazy(() => import('./pages/raz_interactive'));

const router = createBrowserRouter([
  {
    path: '/',
    element: isMobile() ? <H5Website /> : <Website />,
  },
  {
    path: '/wechat',
    element: isMobile() ? <H5Website /> : <Website />,
  },
  {
    path: '/app',
    element: isMobile() ? <H5Website /> : <Website />,
  },
  {
    path: '/self_service',
    element: <H5SelfService />,
  },
  {
    path: '/self_service_create',
    element: <H5SelfServiceCreate />,
  },
  {
    path: '/self_service_baidu',
    element: <BaiduPan />,
  },
  {
    path: '/share_course',
    element: <ShareCourse />,
  },
  {
    path: '/share_course_outside',
    element: <ShareCourseOutside />,
  },
  {
    path: '/vip_sale',
    element: <VipSale />,
  },
  {
    path: '/vip_share',
    element: <VipShare />,
  },
  {
    path: '/vip_share_activity',
    element: IS_HUIYA ? <Navigate to="/" replace /> : <VipShareActivity />,
  },
  {
    path: '/cumulative_invitation',
    element: <CumulativeInvitation />,
  },
  {
    path: '/receive_reward',
    element: <ReceiveReward />,
  },
  {
    path: '/account_details',
    element: <AccountDetails />,
  },
  {
    path: '/activity_description',
    element: <ActivityDescription />,
  },
  {
    path: '/recorded_share',
    element: <ShareRecorded />,
  },
  {
    path: '/about_app',
    element: <AboutApp />,
  },
  {
    path: '/great_teacher',
    element: <GreatTracher />,
  },
  {
    path: '/price_up',
    element: <PriceUp />,
  },
  {
    path: '/daily_paper',
    element: <DailyPaper />,
  },
  {
    path: 'learning_report_detail',
    element: <LearningReportDetail />,
  },
  {
    path: '/form_teacher',
    element: <FormTeacher />,
  },
  {
    path: '/ny_special',
    element: <NYSpecial />,
  },
  {
    path: '/regular_activity',
    element: <RegularActivity />,
  },
  {
    path: '/space_upgrade',
    element: <SpaceUpgrade />,
  },
  {
    path: '/word_share',
    element: <ShareWord />,
  },
  {
    path: '/expand_space',
    element: <ExpandSpace />,
  },
  {
    path: '/price_diffPay',
    element: <PriceDiffPay />,
  },
  {
    path: '/price_diffPay_hy',
    element: <PriceDiffPayHy />,
  },
  {
    path: '/learning_report',
    element: <LearningReport />,
  },
  {
    path: '/booking',
    element: <BookingPlatform />,
  },
  {
    path: '/booking/category',
    element: <Navigate to="/booking?tab=booking" replace />,
  },
  {
    path: '/booking/teacher/:teacherId',
    element: <TeacherDetail />,
  },
  {
    path: '/booking/teacher/:teacherId/edit',
    element: <TeacherEdit />,
  },
  {
    path: '/booking/teacher/add',
    element: <TeacherAdd />,
  },
  {
    path: '/booking/student-edit',
    element: <StudentEdit />,
  },
  {
    path: '/booking/course/:courseId',
    element: <CourseDetail />,
  },
  {
    path: '/booking/textbook-select',
    element: <TextbookSelect />,
  },
  {
    path: '/booking/book-detail/:bookId',
    element: <BookDetail />,
  },
  {
    path: '/booking/confirm',
    element: <BookingConfirm />,
  },
  {
    path: '/booking/login',
    element: <BookingLogin />,
  },
  {
    path: '/booking/favorites',
    element: <FavoritesPage />,
  },
  {
    path: '/booking/feedback',
    element: <FeedbackPage />,
  },
  {
    path: '/booking/help-center',
    element: <HelpCenterPage />,
  },
  {
    path: '/booking/managed-teachers',
    element: <ManagedTeachersPage />,
  },
  {
    path: '/booking/points-record',
    element: <PointsRecordPage />,
  },
  {
    path: '/booking/recharge',
    element: <RechargePage />,
  },
  {
    path: '/booking/teacher/schedule',
    element: <TeacherSchedulePage />,
  },
  {
    path: '/booking/admin-courses',
    element: <AdminCoursesPage />,
  },
  {
    path: '/raz_interactive',
    element: <RazInteractive />,
  },
  {
    path: '/interactive_word_card',
    element: <InteractiveWordCard />,
  },
]);

// 创建UserContext和Provider
// 创建一个Context
export const UserContext = createContext<DeerUser|undefined>(undefined);

const App = () => {

  const [user, setUser] = useState<DeerUser|undefined>();

  useEffect(() => {
    // ===== 临时修改：booking页面不使用全局的App token逻辑 =====
    // booking页面有自己独立的认证系统，不需要从原生获取用户信息
    const isBookingPage = window.location.pathname.includes('/booking');
    if (isBookingPage) {
      console.log('[App] booking页面跳过全局用户信息获取');
      return;
    }
    // ===== 临时修改结束 =====

    if(isApp()){
      // 注册全局消息监听器
      const handleMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'setUserInfo') {
          console.log('setUserInfo123:', event.data.token);
          if (event.data.token) {
            localStorage.setItem('token', event.data.token);
          }
          if(event.data.user){
            setUser(event.data.user);
            console.log('setUserInfo setUser:', event.data.user);
          }
        }
      };
      window.addEventListener('message', handleMessage);
      // // 在应用初始化时调用Flutter的获取用户信息的接口
      JsBridge.getUserInfo({ sourceFrom: "h5" });
    }
    // 清理事件监听器
    // return () => {
    //   window.removeEventListener('message', handleMessage);
    // };
  }, []);

  return (
    <React.StrictMode>
      <ErrorBoundary>
        <UserContext.Provider value={user}>
          <Suspense fallback={
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              background: '#fff',
            }}>
              <div style={{ fontSize: '16px', color: '#999' }}>加载中...</div>
            </div>
          }>
            <RouterProvider router={router} />
          </Suspense>
          <DomainDebugger />
        </UserContext.Provider>
      </ErrorBoundary>
    </React.StrictMode>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
setupChunkLoadRecovery();
root.render(<App />);

//不要等比缩放
// initRem();
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// 尽早调用 setupDomainTesting
setupDomainTesting();
